// A gallery of horizontal AJM Aviation logo concepts, all built to drop into the
// same header slot as the live logo (currentColor wordmark + a sky-blue accent).
const SKY = '#38BDF8';

function Wordmark({ aviationOnly }: { aviationOnly?: boolean }) {
  return (
    <span className="font-display font-bold tracking-tight leading-none">
      {!aviationOnly && 'AJM'}
      {!aviationOnly && ' '}
      <span className="font-semibold opacity-75">AVIATION</span>
    </span>
  );
}

const LOGOS: { name: string; el: (cls: string) => JSX.Element }[] = [
  {
    name: 'Roundel Delta (current)',
    el: (cls) => (
      <span className={`inline-flex items-center gap-2 ${cls}`}>
        <svg viewBox="0 0 32 32" className="h-[1.15em] w-[1.15em]" aria-hidden>
          <circle cx="16" cy="16" r="14.5" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.4" />
          <path d="M16 6 L26.5 26 L16 20.5 L5.5 26 Z" fill={SKY} />
        </svg>
        <Wordmark />
      </span>
    ),
  },
  {
    name: 'Swept Wings',
    el: (cls) => (
      <span className={`inline-flex items-center gap-2 ${cls}`}>
        <svg viewBox="0 0 34 24" className="h-[1.1em] w-auto" aria-hidden>
          <path d="M2 21 L15 3 L20 3 L9 21 Z" fill={SKY} />
          <path d="M12 21 L25 3 L30 3 L21 21 Z" fill="currentColor" />
        </svg>
        <Wordmark />
      </span>
    ),
  },
  {
    name: 'Climb Out',
    el: (cls) => (
      <span className={`inline-flex items-center gap-2 ${cls}`}>
        <svg viewBox="0 0 32 32" className="h-[1.2em] w-[1.2em]" aria-hidden>
          <line x1="3" y1="29" x2="13" y2="29" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeOpacity="0.4" />
          <path d="M5 27 L25 7" stroke={SKY} strokeWidth="3" strokeLinecap="round" />
          <path d="M16.5 7 L25 7 L25 15.5" stroke={SKY} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
        <Wordmark />
      </span>
    ),
  },
  {
    name: 'Tail Fin',
    el: (cls) => (
      <span className={`inline-flex items-center gap-2 ${cls}`}>
        <svg viewBox="0 0 26 32" className="h-[1.25em] w-auto" aria-hidden>
          <path d="M5 29 L13 3 L21 3 L17 29 Z" fill={SKY} />
          <path d="M9 29 L13 16 L17 29 Z" fill="currentColor" fillOpacity="0.25" />
        </svg>
        <Wordmark />
      </span>
    ),
  },
  {
    name: 'Monogram Tile',
    el: (cls) => (
      <span className={`inline-flex items-center gap-2 ${cls}`}>
        <svg viewBox="0 0 34 32" className="h-[1.3em] w-auto" aria-hidden>
          <rect x="1" y="1" width="32" height="30" rx="8" fill="currentColor" />
          <text x="17" y="21.5" textAnchor="middle" fontSize="11.5" fontWeight="800" fill="#fff" fontFamily="-apple-system, Inter, sans-serif" letterSpacing="-0.5">
            AJM
          </text>
        </svg>
        <Wordmark aviationOnly />
      </span>
    ),
  },
  {
    name: 'Altitude Bars',
    el: (cls) => (
      <span className={`inline-flex items-center gap-2 ${cls}`}>
        <svg viewBox="0 0 30 32" className="h-[1.2em] w-auto" aria-hidden>
          <rect x="2" y="20" width="5" height="9" rx="2" fill="currentColor" fillOpacity="0.35" />
          <rect x="11" y="13" width="5" height="16" rx="2" fill="currentColor" fillOpacity="0.6" />
          <rect x="20" y="5" width="5" height="24" rx="2" fill={SKY} />
        </svg>
        <Wordmark />
      </span>
    ),
  },
  {
    name: 'Heading Roundel',
    el: (cls) => (
      <span className={`inline-flex items-center gap-2 ${cls}`}>
        <svg viewBox="0 0 32 32" className="h-[1.2em] w-[1.2em]" aria-hidden>
          <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1.3" />
          <circle cx="16" cy="16" r="2" fill="currentColor" />
          <path d="M16 16 L26 9" stroke={SKY} strokeWidth="2.6" strokeLinecap="round" />
          <path d="M22.5 6.5 L27 8.5 L25 13" stroke={SKY} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
        <Wordmark />
      </span>
    ),
  },
  {
    name: 'Underline Jet',
    el: (cls) => (
      <span className={`inline-flex flex-col items-start ${cls}`}>
        <span className="inline-flex items-center gap-1.5">
          <svg viewBox="0 0 24 24" className="h-[0.95em] w-auto" aria-hidden>
            <path d="M12 2 L19 21 L12 17 L5 21 Z" fill={SKY} />
          </svg>
          <Wordmark />
        </span>
        <svg viewBox="0 0 120 4" className="mt-0.5 h-[3px] w-full" preserveAspectRatio="none" aria-hidden>
          <line x1="0" y1="2" x2="120" y2="2" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" strokeDasharray="6 5" />
        </svg>
      </span>
    ),
  },
];

const AI_LOGOS = [
  { n: 1, name: 'Wing Monogram' },
  { n: 2, name: 'Propeller Monogram' },
  { n: 3, name: 'Speed Wing' },
  { n: 4, name: 'Gold Roundel' },
  { n: 5, name: 'Climbing Jet' },
];

export function LogoGallery() {
  return (
    <div className="min-h-screen bg-canvas px-4 py-10 sm:px-8">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-3xl font-bold tracking-tight text-navy-900">AJM logo concepts</h1>
        <p className="mt-1 text-ink-muted">
          AI-generated concepts up top, earlier hand-drawn ones below. Tell me the name you like (or “make
          more like #X”) and I’ll refine + set it live.
        </p>

        <div className="mt-6 text-xs font-semibold uppercase tracking-wider text-ink-subtle">Nano Banana concepts</div>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {AI_LOGOS.map((l) => (
            <div key={l.n} className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-900/[0.06]">
              <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
                {l.name}
              </div>
              <img src={`${import.meta.env.BASE_URL}logo-nb-${l.n}.png`} alt={l.name} className="block w-full" />
            </div>
          ))}
        </div>

        <div className="mt-10 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Earlier hand-drawn concepts
        </div>
        <div className="mt-2 space-y-5">
          {LOGOS.map((logo) => (
            <div key={logo.name} className="overflow-hidden rounded-2xl ring-1 ring-slate-900/[0.06]">
              <div className="border-b border-slate-100 bg-white px-5 py-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
                {logo.name}
              </div>
              {/* Large on white */}
              <div className="flex items-center justify-center bg-white px-5 py-8 text-navy-900">
                {logo.el('text-3xl')}
              </div>
              {/* Header size on the frosted bar + large on navy */}
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="flex items-center gap-3 border-t border-slate-100 bg-white/80 px-5 py-4 text-navy-900">
                  <span className="text-[10px] uppercase text-ink-subtle">header</span>
                  {logo.el('text-lg')}
                </div>
                <div className="flex items-center justify-center border-t border-slate-100 bg-brand px-5 py-6 text-white">
                  {logo.el('text-2xl')}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
